import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SUBSCRIPTION_PLANS } from '../utils/stripe';

/**
 * Hook for managing FBO onboarding with new schema
 * Uses fbos table and related tables for state management
 */
export const useFboOnboarding = () => {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id || null;
  const userEmail = user?.email || null;
  const userEmailLower = user?.email?.toLowerCase() ?? null;
  const userFullName = user?.user_metadata?.full_name ?? null;
  const userPhone = user?.user_metadata?.phone ?? null;
  const [fbo, setFbo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load existing FBO for current user
   */
  const loadFbo = useCallback(async () => {
    if (!userId) return;

    console.log('🔄 Loading FBO for user:', userId);
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('fbos')
        .select('*, seller_menu_drafts(*)')
        .eq('user_id', userId)
        .maybeSingle();

      console.log('📊 Raw Supabase response:', { data, fetchError, userId });

      if (fetchError) throw fetchError;

      const normalizedFbo = data
        ? (() => {
            const draft =
              Array.isArray(data.seller_menu_drafts) && data.seller_menu_drafts.length > 0
                ? data.seller_menu_drafts[0]
                : null;
            const { seller_menu_drafts, ...rest } = data;
            return {
              ...rest,
              menuDraft: draft,
            };
          })()
        : null;

      console.log('📊 FBO loaded:', normalizedFbo);
      setFbo(normalizedFbo);
    } catch (err) {
      console.error('❌ Error loading FBO:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Load FBO data on mount or when user changes
  useEffect(() => {
    console.log('🔍 useFboOnboarding useEffect:', { isAuthenticated, userId });
    if (isAuthenticated && userId) {
      loadFbo();
    } else {
      setFbo(null);
    }
  }, [userId, isAuthenticated, loadFbo]);

  /**
   * Create or update FBO record (auto-save)
   */
  const saveFbo = useCallback(async (formData, isSubmit = false) => {
    if (!userId) {
      return { requiresAuth: true };
    }

    setLoading(true);
    setError(null);

    try {
      let normalizedAddress = null;
      if (formData.homeAddress) {
        const {
          line1,
          line2,
          street,
          city,
          postcode,
          country,
          lat,
          lng,
        } = formData.homeAddress;
        const primaryLine = line1 ?? street;
        if (primaryLine || city || postcode) {
          normalizedAddress = {
            line1: (primaryLine || '').trim(),
            line2: line2 ? line2.trim() : null,
            city: (city || '').trim(),
            postcode: (postcode || '').trim().toUpperCase(),
            country: country || 'United Kingdom',
          };
          if (typeof lat === 'number') {
            normalizedAddress.lat = lat;
          }
          if (typeof lng === 'number') {
            normalizedAddress.lng = lng;
          }
        }
      }

      let profileEnsured = false;
      try {
        const baseUserRecord = {
          id: userId,
          email: userEmailLower,
          full_name:
            formData.displayName ??
            userFullName ??
            fbo?.display_name ??
            null,
          phone: formData.phoneNumber || userPhone || null,
          user_type: 'fbo',
          updated_at: new Date().toISOString(),
        };

        const { error: upsertUserError } = await supabase
          .from('users')
          .upsert(baseUserRecord, { onConflict: 'id' });

        if (upsertUserError) {
          console.warn('⚠️ Unable to upsert users profile:', upsertUserError.message);
        } else {
          profileEnsured = true;
        }
      } catch (profileError) {
        console.warn('⚠️ Unexpected error upserting users profile:', profileError.message);
      }

      if (!profileEnsured) {
        try {
          const minimalRecord = {
            id: userId,
            email: userEmailLower,
            updated_at: new Date().toISOString(),
          };

          const { error: fallbackError } = await supabase
            .from('users')
            .upsert(minimalRecord, { onConflict: 'id' });

          if (fallbackError) {
            console.warn('⚠️ Fallback profile upsert failed:', fallbackError.message);
          } else {
            profileEnsured = true;
          }
        } catch (fallbackException) {
          console.warn('⚠️ Fallback profile upsert threw:', fallbackException.message);
        }
      }

      let normalizedRating = null;
      if (formData.fhrsRating !== undefined && formData.fhrsRating !== null) {
        if (formData.fhrsRating === 'AwaitingInspection' || formData.fhrsRating === -1) {
          normalizedRating = -1;
        } else if (!Number.isNaN(Number(formData.fhrsRating))) {
          normalizedRating = Number(formData.fhrsRating);
        }
      }

      const nextMenuStatus =
        formData.menuStatus ||
        (formData.menuDraft?.items?.length ? 'draft' : 'not_started');

      const fboData = {
        user_id: userId,
        display_name:
          formData.displayName ??
          fbo?.display_name ??
          userFullName ??
          '',
        phone_verified: !!formData.phoneVerified,
        home_address: normalizedAddress,
        onboarding_stage: formData.onboardingStage || fbo?.onboarding_stage || 'profile',
        fhrs_business_id: formData.fhrsBusinessId || null,
        fhrs_rating: normalizedRating,
        fhrs_rating_date: formData.fhrsRatingDate || null,
        registration_status: formData.registrationStatus || 'not_registered',
        willing_to_improve:
          typeof formData.willingToImprove === 'boolean'
            ? formData.willingToImprove
            : null,
        needs_registration_support:
          typeof formData.needsRegistrationSupport === 'boolean'
            ? formData.needsRegistrationSupport
            : false,
        menu_status: nextMenuStatus,
        subscription_status:
          formData.subscriptionStatus || fbo?.subscription_status || 'none',
        stripe_account_id: formData.stripeAccountId || fbo?.stripe_account_id || null,
        stripe_connect_onboarded_at:
          formData.stripeConnectOnboardedAt || fbo?.stripe_connect_onboarded_at || null,
        kyc_status: formData.kycStatus || fbo?.kyc_status || 'pending',
        notes: formData.notes || fbo?.notes || null,
        updated_at: new Date().toISOString(),
      };

      let result;
      if (fbo?.id) {
        // Update existing FBO
        const { data, error: updateError } = await supabase
          .from('fbos')
          .update(fboData)
          .eq('id', fbo.id)
          .select()
          .single();

        if (updateError) throw updateError;
        result = data;
      } else {
        // Create new FBO
        const { data, error: insertError } = await supabase
          .from('fbos')
          .insert(fboData)
          .select()
          .single();

        if (insertError) throw insertError;
        result = data;
      }

      console.log('💾 FBO saved:', result);
      setFbo(prev => (prev ? { ...prev, ...result } : result));
      return { success: true, data: result };
    } catch (err) {
      console.error('❌ Error saving FBO:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [userId, userEmailLower, userFullName, userPhone, fbo]);

  /**
   * Save menu draft
   */
  const saveMenuDraft = useCallback(
    async (draftData) => {
      if (!fbo?.id) return { success: false, error: 'No FBO record found' };
      if (!draftData) return { success: false, error: 'No draft data provided' };

      try {
        const payload = {
          fbo_id: fbo.id,
          source: draftData.source || 'manual',
          items: draftData.items || [],
          updated_at: new Date().toISOString(),
        };

        const { data, error } = await supabase
          .from('seller_menu_drafts')
          .upsert(payload, { onConflict: 'fbo_id' })
          .select()
          .single();

        if (error) throw error;

        setFbo(prev =>
          prev ? { ...prev, menu_status: 'draft', menuDraft: data } : prev
        );

        return { success: true, data };
      } catch (err) {
        console.error('❌ Error saving menu draft:', err);
        return { success: false, error: err.message };
      }
    },
    [fbo?.id]
  );

  /**
   * Search FHRS business
   */
  const searchFhrsBusiness = useCallback(async (name, postcode) => {
    try {
      const { data, error } = await supabase.functions.invoke('fhrs-search', {
        body: { name, postcode }
      });

      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('❌ Error searching FHRS:', err);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Create Stripe Connect account
   */
  const createStripeConnectAccount = useCallback(
    async ({ businessName } = {}) => {
      if (!userId) return { success: false, error: 'No user found' };
      if (!userEmail) return { success: false, error: 'Missing user email for Stripe Connect' };

      const effectiveBusinessName =
        businessName ||
        fbo?.display_name ||
        fbo?.fhrs_business_name ||
        userFullName ||
        userEmail;

      try {
        const { data, error } = await supabase.functions.invoke(
          'create-connect-account',
          {
            body: {
              userId,
              businessName: effectiveBusinessName,
              email: userEmail,
            },
          }
        );

        if (error) throw error;

        if (data?.accountId && fbo?.id) {
          const { error: updateError } = await supabase
            .from('fbos')
            .update({
              stripe_account_id: data.accountId,
              kyc_status: 'pending',
              updated_at: new Date().toISOString(),
            })
            .eq('id', fbo.id);

          if (updateError) {
            console.warn('⚠️ Unable to record Stripe account on FBO:', updateError.message);
          }
        }

        await loadFbo();

        return {
          success: true,
          data: {
            ...data,
            url: data?.accountLinkUrl,
          },
        };
      } catch (err) {
        console.error('❌ Error creating Stripe Connect account:', err);
        return { success: false, error: err.message };
      }
    },
    [userId, userEmail, userFullName, fbo?.id, fbo?.display_name, fbo?.fhrs_business_name, loadFbo]
  );

  /**
   * Create checkout session for subscription
   */
  const createCheckoutSession = useCallback(
    async (planTier = 'starter', billingFrequency = 'monthly') => {
      if (!fbo?.id) return { success: false, error: 'No FBO record found' };
      if (!userId) return { success: false, error: 'No user found' };

      const tier = planTier in SUBSCRIPTION_PLANS ? planTier : 'starter';
      const billingKey =
        billingFrequency === 'annual' || billingFrequency === 'yearly'
          ? 'annual'
          : 'monthly';
      const planConfig =
        SUBSCRIPTION_PLANS[tier]?.[billingKey] ||
        SUBSCRIPTION_PLANS[tier]?.yearly;

      if (!planConfig?.priceId) {
        return {
          success: false,
          error:
            'Stripe price ID is not configured for the selected plan. Please set REACT_APP_STRIPE_PRICE_STARTER_MONTHLY / ANNUAL.',
        };
      }

      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const successUrl = origin ? `${origin}/seller/get-started?checkout=success` : undefined;
      const cancelUrl = origin ? `${origin}/seller/get-started?checkout=cancelled` : undefined;

      try {
        const payload = {
          priceId: planConfig.priceId,
          planTier: tier,
          billingPeriod: billingKey,
          fboId: fbo.id,
          userId,
          userEmail,
          successUrl: successUrl || undefined,
          cancelUrl: cancelUrl || undefined,
        };

        const { data, error } = await supabase.functions.invoke(
          'create-checkout-session',
          {
            body: payload,
          }
        );

        if (error) throw error;
        return { success: true, data };
      } catch (err) {
        console.error('❌ Error creating checkout session:', err);
        return { success: false, error: err.message };
      }
    },
    [fbo?.id, userId, userEmail]
  );

  return {
    fbo,
    loading,
    error,
    loadFbo,
    saveFbo,
    saveMenuDraft,
    searchFhrsBusiness,
    createStripeConnectAccount,
    createCheckoutSession
  };
};

export default useFboOnboarding;
